import React, { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Heart } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/workshops", label: "Workshops" },
  { to: "/programmes", label: "Our Programme" },
  { to: "/leadership", label: "Leadership" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "bg-bone/85 border-b border-ink/10" : "bg-bone/60 border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/logo.jpg" alt="Aotūroa Academy logo" className="h-11 w-auto rounded-md" />
          <span className="hidden md:block leading-tight max-w-[220px]">
            <span className="block font-heading font-semibold text-[14px] text-ink">Aoturoa Academy</span>
            <span className="block text-[10px] uppercase tracking-[0.18em] text-ink/55">Charitable Trust · CC58012</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-7" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to="/donate" data-testid="header-donate-btn" className="text-sm font-medium text-ink/75 hover:text-ink transition-colors flex items-center gap-1.5">
            <Heart className="w-4 h-4 text-crimson" /> Donate
          </Link>
          <Link to="/contact" data-testid="header-enquire-btn" className="btn-crimson !py-2.5 !px-5">
            Enquire About a Workshop
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="xl:hidden w-11 h-11 rounded-full border border-ink/15 flex items-center justify-center"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-ink/10 bg-bone" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 px-4 rounded-xl font-medium ${isActive ? "bg-ink text-white" : "text-ink hover:bg-sand"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/donate"
              onClick={() => setOpen(false)}
              data-testid="mobile-donate-btn"
              className="mt-3 btn-outline w-full"
            >
              <Heart className="w-4 h-4 text-crimson" /> Donate
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-enquire-btn"
              className="mt-2 btn-crimson w-full"
            >
              Enquire About a Workshop
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
